/**
 * Trottinettes : modèles, vues et identifiants du catalogue (partagé par le
 * jeu et le serveur temps réel, sous-chemin `@campus/core/scooters`).
 *
 * Une trottinette posée sur la carte est un meuble `pl_trottinette_{modèle}_{vue}`.
 * On monte dessus : elle disparaît pour tout le monde pendant qu'on roule
 * (PlayerState.vehicle = modèle), puis se repose là où l'on descend, tournée
 * dans le sens de l'avatar. État gardé par la ZoneRoom seulement : rien en
 * base, chaque trottinette reprend sa place d'origine quand la salle se vide.
 */

export const SCOOTER_MODELS = ['electrique_noire', 'electrique_blanche', 'partagee', 'classique'] as const;
export type ScooterModel = (typeof SCOOTER_MODELS)[number];
export type ScooterDirection = 'up' | 'down' | 'left' | 'right';

/** Vitesse sur la trottinette, par rapport à la marche (le sprint est désactivé). */
export const SCOOTER_SPEED_MULTIPLIER = 2;
/** Distance max (px) entre l'avatar et la trottinette pour monter dessus. */
export const SCOOTER_MOUNT_DISTANCE = 56;

const VIEWS: Record<ScooterDirection, string> = { right: 'droite', left: 'gauche', down: 'face', up: 'dos' };
const DIRECTIONS = Object.fromEntries(Object.entries(VIEWS).map(([d, v]) => [v, d])) as Record<string, ScooterDirection>;
const ID_PATTERN = new RegExp(`^pl_trottinette_(${SCOOTER_MODELS.join('|')})_(${Object.values(VIEWS).join('|')})$`);

export function isScooterModel(value: unknown): value is ScooterModel {
  return typeof value === 'string' && (SCOOTER_MODELS as readonly string[]).includes(value);
}

export function isScooterDirection(value: unknown): value is ScooterDirection {
  return value === 'up' || value === 'down' || value === 'left' || value === 'right';
}

/** Meuble du catalogue qui représente ce modèle tourné dans cette direction. */
export function scooterFurnitureId(model: ScooterModel, direction: ScooterDirection): string {
  return `pl_trottinette_${model}_${VIEWS[direction]}`;
}

/** Modèle et direction d'un meuble trottinette, null pour tout autre meuble. */
export function parseScooterFurnitureId(id: string): { model: ScooterModel; direction: ScooterDirection } | null {
  const m = ID_PATTERN.exec(id);
  if (!m) return null;
  return { model: m[1] as ScooterModel, direction: DIRECTIONS[m[2]!]! };
}

/** Trottinette déplacée ou en cours d'utilisation, telle que diffusée par la ZoneRoom. */
export interface ScooterSnapshot {
  /** Identifiant du meuble posé (map_furniture.id). */
  itemId: string;
  model: ScooterModel;
  /** Position au sol (milieu du bas de la case), en px. */
  x: number;
  y: number;
  direction: ScooterDirection;
  /** userId de la personne qui roule dessus, null si elle est garée. */
  riderUserId: string | null;
}
